import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { auth } from '../firebase';

// Skaber kontekst til kundeveksling
const CustomerSwitchContext = createContext();

// Hook til at bruge kundeveksling i komponenter
export const useCustomerSwitch = () => {
  return useContext(CustomerSwitchContext);
};

export const CustomerSwitchProvider = ({ children }) => {
  const { currentUser, userType } = useAuth();
  const [assignedCustomers, setAssignedCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState(null);
  const [debugInfo, setDebugInfo] = useState({ step: 'init', info: {} });
  const [error, setError] = useState(null);

  // Hent organisationsID for den nuværende bruger
  useEffect(() => {
    const fetchOrganizationId = async () => {
      if (!currentUser) return;
      
      try {
        console.log('CustomerSwitchContext: Henter organizationId for bruger:', currentUser.uid);
        setDebugInfo({ step: 'fetching-org-id', info: { userId: currentUser.uid } });
        
        // Konverteret til Firebase v9 syntax
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          console.log('CustomerSwitchContext: Brugerdata hentet:', userData);
          setDebugInfo(prev => ({ 
            ...prev, 
            info: { ...prev.info, userData: userData } 
          }));
          
          // Den oprindelige metode - bruger organizationId fra brugerens data
          if (userData.organizationId) {
            console.log('CustomerSwitchContext: Fandt organizationId fra brugerdata:', userData.organizationId);
            setOrganizationId(userData.organizationId);
            setDebugInfo(prev => ({ 
              ...prev, 
              info: { ...prev.info, organizationId: userData.organizationId, source: 'userData' } 
            }));
            return;
          }
          
          // Alternativ metode 1: Tjek om brugeren har organizations array
          if (userData.organizations && userData.organizations.length > 0) {
            console.log('CustomerSwitchContext: Fandt organizations array i brugerdata:', userData.organizations);
            // Brug den første organisation i listen
            const firstOrgId = userData.organizations[0];
            setOrganizationId(firstOrgId);
            setDebugInfo(prev => ({ 
              ...prev, 
              info: { ...prev.info, organizationId: firstOrgId, source: 'organizations-array' } 
            }));
            return;
          }
          
          // Alternativ metode 2: Søg efter medlemskab i alle organisationer
          try {
            // Søg efter alle organisationer i databasen
            const orgsRef = collection(db, 'organizations');
            const orgsSnap = await getDocs(orgsRef);
            
            console.log(`CustomerSwitchContext: Søger gennem ${orgsSnap.size} organisationer for medlemskab`);
            
            for (const orgDoc of orgsSnap.docs) {
              const orgId = orgDoc.id;
              
              // Tjek medlemskab direkte ved at søge efter brugerens dokument i members collection
              const memberRef = doc(db, 'organizations', orgId, 'members', currentUser.uid);
              const memberSnap = await getDoc(memberRef);
              
              if (memberSnap.exists()) {
                console.log(`CustomerSwitchContext: Fandt bruger som direkte medlem i organisation: ${orgId}`);
                setOrganizationId(orgId);
                setDebugInfo(prev => ({ 
                  ...prev, 
                  info: { ...prev.info, organizationId: orgId, source: 'direct-member' } 
                }));
                return;
              }
              
              // Tjek om brugeren er medlem via userId felt i members collection
              const membersRef = collection(db, 'organizations', orgId, 'members');
              const membersQuery = query(membersRef, where('userId', '==', currentUser.uid));
              const membersSnap = await getDocs(membersQuery);
              
              if (!membersSnap.empty) {
                console.log(`CustomerSwitchContext: Fandt bruger som medlem via userId i organisation: ${orgId}`);
                setOrganizationId(orgId);
                setDebugInfo(prev => ({ 
                  ...prev, 
                  info: { ...prev.info, organizationId: orgId, source: 'userId-member' } 
                }));
                return;
              }
              
              // Tjek om brugeren har en licens i organisationen
              const licensesRef = collection(db, 'organizations', orgId, 'licenses');
              const licensesQuery = query(licensesRef, where('assignedTo', '==', currentUser.uid));
              const licensesSnap = await getDocs(licensesQuery);
              
              if (!licensesSnap.empty) {
                console.log(`CustomerSwitchContext: Fandt bruger med licens i organisation: ${orgId}`);
                setOrganizationId(orgId);
                setDebugInfo(prev => ({ 
                  ...prev, 
                  info: { ...prev.info, organizationId: orgId, source: 'license' } 
                }));
                return;
              }
            }
            
            console.log('CustomerSwitchContext: Kunne ikke finde brugerens organisation efter alle søgninger');
            setDebugInfo(prev => ({ 
              ...prev, 
              info: { ...prev.info, organizationNotFound: true } 
            }));
          } catch (searchError) {
            console.error('CustomerSwitchContext: Fejl ved søgning efter organisationer:', searchError);
            setDebugInfo(prev => ({ 
              ...prev, 
              info: { ...prev.info, searchError: searchError.message } 
            }));
          }
        } else {
          console.log('CustomerSwitchContext: Ingen brugerdata fundet');
          setDebugInfo(prev => ({ 
            ...prev, 
            info: { ...prev.info, userExists: false } 
          }));
        }
      } catch (error) {
        console.error('Fejl ved hentning af organisationsID:', error);
        setDebugInfo({ 
          step: 'error-fetching-org-id', 
          error: error.message 
        });
      }
    };
    
    fetchOrganizationId();
  }, [currentUser]);

  // Hent tildelte kunder når organisationsID er tilgængeligt
  useEffect(() => {
    if (!organizationId || !currentUser || userType === 'customer') {
      setLoading(false);
      console.log('CustomerSwitchContext: Springer kundetildeling over', { 
        hasOrgId: !!organizationId, 
        hasUser: !!currentUser, 
        userType 
      });
      setDebugInfo({ 
        step: 'skipping-customer-fetch',
        info: { 
          hasOrgId: !!organizationId, 
          hasUser: !!currentUser, 
          userType 
        }
      });
      return;
    }
    
    try {
      console.log('CustomerSwitchContext: Henter kundetildelinger for medarbejder:', currentUser.uid);
      setDebugInfo({ 
        step: 'fetching-assignments',
        info: { 
          organizationId, 
          userId: currentUser.uid 
        }
      });
      
      // Trin 1: Find medarbejder-dokument i members collection
      // Dette er vigtigt, fordi employeeId kan være dokument-ID fra members collection
      const fetchMemberDoc = async () => {
        try {
          console.log('CustomerSwitchContext: Forsøger at finde medlemsdokument for bruger');
          // Først prøv at finde i members collection via userId
          const membersRef = collection(db, 'organizations', organizationId, 'members');
          const membersQuery = query(membersRef, where('userId', '==', currentUser.uid));
          const membersSnapshot = await getDocs(membersQuery);
          
          if (!membersSnapshot.empty) {
            const memberDoc = membersSnapshot.docs[0];
            console.log('CustomerSwitchContext: Fandt medlemsdokument med ID:', memberDoc.id);
            return memberDoc.id;
          } 
          
          // Hvis vi ikke kan finde ved userId, prøv at finde ved direkte opslag på brugerens uid
          const directMemberRef = doc(db, 'organizations', organizationId, 'members', currentUser.uid);
          const directMemberSnap = await getDoc(directMemberRef);
          
          if (directMemberSnap.exists()) {
            console.log('CustomerSwitchContext: Fandt medlemsdokument direkte med ID:', currentUser.uid);
            return currentUser.uid;
          }
          
          // Tjek om brugeren har en licens i organisationen
          const licensesRef = collection(db, 'organizations', organizationId, 'licenses');
          const licensesQuery = query(licensesRef, where('assignedTo', '==', currentUser.uid));
          const licensesSnapshot = await getDocs(licensesQuery);
          
          if (!licensesSnapshot.empty) {
            console.log('CustomerSwitchContext: Brugeren har en licens i organisationen');
            // Bruger uid som ID, da brugeren har en licens
            return currentUser.uid;
          }
          
          console.log('CustomerSwitchContext: Fandt ikke et medlemsdokument og ingen licens');
          return null;
        } catch (error) {
          console.error('CustomerSwitchContext: Fejl ved hentning af medlemsdokument:', error);
          return null;
        }
      };
      
      // Trin 2: Opsæt lytteren til at få alle kundetildelinger og filtrer
      const setupAssignmentsListener = async () => {
        const memberDocId = await fetchMemberDoc();
        console.log('CustomerSwitchContext: Medarbejder dokument ID:', memberDocId);
        
        if (!memberDocId) {
          console.log('CustomerSwitchContext: Ingen medlems-ID eller licens fundet, kan ikke hente kundetildelinger');
          setLoading(false);
          return () => {}; // Tom unsubscribe funktion
        }
        
        // Hent alle kundetildelinger
        const assignmentsRef = collection(db, 'organizations', organizationId, 'customerAssignments');
        
        // Lyt til ændringer i kundetildelinger i realtid
        return onSnapshot(assignmentsRef, async (assignmentsSnapshot) => {
          console.log('CustomerSwitchContext: Modtog snapshot med', assignmentsSnapshot.size, 'kundetildelinger');
          
          // Filter data på klientsiden
          const customerIds = [];
          assignmentsSnapshot.forEach(doc => {
            const data = doc.data();
            console.log('CustomerSwitchContext: Kundetildeling:', doc.id, data);
            
            // Tjek om tildelingen er til den aktuelle bruger
            // - enten via employeeId === brugerens uid
            // - eller employeeId === brugerens dokument-id (som vi måske fandt ovenfor)
            if (data.employeeId === currentUser.uid || data.employeeId === memberDocId) {
              console.log('CustomerSwitchContext: Match fundet på tildeling:', data.employeeId);
              if (data.customerId) {
                customerIds.push(data.customerId);
              }
            } else {
              console.log('CustomerSwitchContext: Tildeling var ikke til denne bruger');
            }
          });
          
          console.log('CustomerSwitchContext: Fandt', customerIds.length, 'kunde-ID\'er efter filtrering');
          setDebugInfo(prev => ({ 
            ...prev, 
            info: { 
              ...prev.info, 
              customerIds,
              allAssignments: assignmentsSnapshot.size,
              memberDocId
            }
          }));
          
          // Hvis der ikke er tildelte kunder, nulstil og stop
          if (customerIds.length === 0) {
            console.log('CustomerSwitchContext: Ingen kundetildelinger fundet');
            setAssignedCustomers([]);
            setSelectedCustomer(null);
            setLoading(false);
            return;
          }
          
          // Hent kundedata for alle tildelte kunder
          const customers = [];
          for (const customerId of customerIds) {
            console.log('CustomerSwitchContext: Henter data for kunde:', customerId);
            
            // Prøv først at hente fra users collection
            try {
              // Konverteret til Firebase v9 syntax
              const userRef = doc(db, 'users', customerId);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                const userData = userSnap.data();
                console.log('CustomerSwitchContext: Brugerdata fundet:', userData);
                customers.push({
                  id: customerId,
                  ...userData,
                  source: 'user'
                });
                continue; // Gå videre til næste customerId
              }
            } catch (error) {
              console.error(`Fejl ved hentning af kundedata for ${customerId} fra users:`, error);
            }
            
            // Hvis users ikke virkede, prøv members collection
            try {
              const membersRef = collection(db, 'organizations', organizationId, 'members');
              const memberQ = query(membersRef, where('userId', '==', customerId));
              let memberSnap = await getDocs(memberQ);
              
              if (!memberSnap.empty) {
                // Kunde fundet i members via userId
                memberSnap.forEach(doc => {
                  const memberData = doc.data();
                  console.log('CustomerSwitchContext: Medlemsdata fundet via userId:', memberData);
                  customers.push({
                    id: customerId,
                    ...memberData,
                    source: 'member-userId'
                  });
                });
                continue;
              }
              
              // Prøv at hente direkte via dokument-ID
              try {
                const memberDocRef = doc(db, 'organizations', organizationId, 'members', customerId);
                const memberDoc = await getDoc(memberDocRef);
                
                if (memberDoc.exists()) {
                  const memberData = memberDoc.data();
                  console.log('CustomerSwitchContext: Medlemsdata fundet via docId:', memberData);
                  customers.push({
                    id: customerId,
                    ...memberData,
                    source: 'member-docId'
                  });
                } else {
                  console.log('CustomerSwitchContext: Kunde ikke fundet via dokument-ID');
                }
              } catch (error) {
                console.error(`Fejl ved hentning af kundedata for ${customerId} via dokument-ID:`, error);
              }
            } catch (error) {
              console.error(`Fejl ved hentning af kundedata for ${customerId} fra members:`, error);
            }
          }
          
          console.log('CustomerSwitchContext: Fandt', customers.length, 'kunder:', customers);
          setAssignedCustomers(customers);
          setDebugInfo(prev => ({ 
            ...prev, 
            info: { 
              ...prev.info, 
              customers 
            }
          }));
          
          setLoading(false);
        }, (error) => {
          console.error('Fejl ved lytning til kundetildelinger:', error);
          setDebugInfo({ 
            step: 'error-listening-to-assignments', 
            error: error.message 
          });
          setLoading(false);
        });
      };
      
      // Start lytteren
      let unsubscribe;
      setupAssignmentsListener().then(unsub => {
        unsubscribe = unsub;
      });
      
      return () => {
        if (unsubscribe) unsubscribe();
      };
    } catch (error) {
      console.error('Fejl ved hentning af tildelte kunder:', error);
      setDebugInfo({ 
        step: 'error-fetching-assigned-customers', 
        error: error.message 
      });
      setLoading(false);
    }
  }, [organizationId, currentUser, userType]);

  // Skift mellem kunder
  const switchToCustomer = (customerId) => {
    if (!customerId) {
      console.log('CustomerSwitchContext: Skifter til egen profil');
      // Skift til egen profil
      setSelectedCustomer(null);
      return;
    }
    
    const customer = assignedCustomers.find(c => c.id === customerId);
    console.log('CustomerSwitchContext: Skifter til kunde:', customer);
    setSelectedCustomer(customer);
  };

  // Eksporter state gennem window-objektet for at give ClaudeService adgang
  useEffect(() => {
    // Eksporter context state til et globalt objekt
    window.customerSwitchState = {
      selectedCustomer,
      isWorkingOnBehalfOfCustomer: !!selectedCustomer
    };
    
    // Debug log
    console.log('CustomerSwitchContext state opdateret globalt:', {
      selectedCustomerId: selectedCustomer?.id,
      isWorkingOnBehalfOfCustomer: !!selectedCustomer
    });
    
    // Cleanup når komponenten unmountes
    return () => {
      window.customerSwitchState = null;
    };
  }, [selectedCustomer]);

  // Værdier der eksporteres fra konteksten
  const value = {
    assignedCustomers,
    selectedCustomer,
    switchToCustomer,
    loading,
    error,
    isWorkingOnBehalfOfCustomer: !!selectedCustomer,
    debug: debugInfo
  };

  return (
    <CustomerSwitchContext.Provider value={value}>
      {children}
    </CustomerSwitchContext.Provider>
  );
}; 